import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ViewStyle } from "react-native"
import { JalaLoading, JalaWebView, LazyRender, Screen } from "../../../components"
import { color } from "../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { View } from "native-base"
import WebView from "react-native-webview"
import { WebViewNavigation } from "react-native-webview/lib/WebViewTypes"

const ROOT: ViewStyle = {
  flex: 1,
}

const WEB_VIEW: ViewStyle = {
  flexGrow: 1,
}

const LoadingState: FC<{ colorScheme: "light"|"dark" }> = function(props) {
  return (
    <View flex={1} px="16px" justifyContent="center" alignItems="center">
      <JalaLoading />
    </View>
  )
}

export const JalaWebViewScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function JalaWebViewScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params = {} }, navigation } = props;

  const webView = useRef<WebView>()
  const canGoBack = useRef<boolean>(false)
  const goBackRetry = useRef<number>(0)

  useEffect(() => {
    const callback = (e) => {
      if (!webView.current || !canGoBack.current) return;
      e.preventDefault();
      try {
        webView.current.goBack();
        goBackRetry.current = goBackRetry.current + 1;
        if (goBackRetry.current > 1)
          navigation.dispatch(e.data.action);
      } catch (e) {
        navigation.dispatch(e.data.action);
      }
    }
    navigation.addListener('beforeRemove', callback)
    return () => navigation.removeListener('beforeRemove', callback)
  }, [navigation]);

  const source = useMemo(() => {
    return { uri: params.uri }
  }, [params.uri])

  useLayoutEffect(() => {
    navigation.setOptions({
      title: params.title || '',
      headerTitle: params.title || '',
      headerShown: !params.hideNavbar,
    });
  }, [navigation, params]);

  const onNavigationStateChange: (event: WebViewNavigation) => void = useCallback((WebViewNavigation) => {
    canGoBack.current = WebViewNavigation.canGoBack
    goBackRetry.current = 0;
  }, [])

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      hideOnBlurred={false}
      safeAreaEdges={!params.hideNavbar ? ['bottom'] : ['top', 'bottom']}
      colorScheme={colorScheme}
      backgroundColor={params.backgroundColor || (colorScheme === 'light' ? color.primary : undefined)}
      backgroundBar={params.backgroundBar || (colorScheme === 'light' ? color.primary : undefined)}
      statusBar={params.statusBar || (colorScheme === "light" ? "light-content" : "dark-content")}
    >
      <LazyRender renderPlaceholder={LoadingState}>
        {source && <JalaWebView
          ref={webView}
          source={source}
          style={WEB_VIEW}
          startInLoadingState
          onNavigationStateChange={onNavigationStateChange}
        />}
      </LazyRender>
    </Screen>
  )
})
